import { Component, DestroyRef, OnInit, output, ElementRef, input, inject } from '@angular/core';
import { Validators, UntypedFormGroup, FormControl, UntypedFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { CollaborationsService, CustomersService } from '@app/core/stingray-api/services';
import { Collaboration, GuidId, Customer, Member, NewCollaboration } from '@app/core/stingray-api/models';
import { debounceTime, tap, switchMap, finalize, distinctUntilChanged, map, catchError } from 'rxjs/operators';
import * as _ from 'lodash';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { of } from 'rxjs';
import { GoogleAnalyticsService } from '@app/core/analytics/google-analytics.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { createAddressFormGroup } from '@app/shared/address-input-v2/address-input-v2-tools';
import { LoadingButtonComponent } from '../../loading-button/loading-button.component';
import { CustomerSettingsComponent } from '../../customer-settings/customer-settings.component';
import { AddressInputV2Component } from '../../address-input-v2/address-input-v2.component';
import { MatIconButton } from '@angular/material/button';
import { MatList, MatListItem, MatListItemIcon } from '@angular/material/list';
import { MatOption, MatLine } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

import { MatInput } from '@angular/material/input';
import { MatFormField, MatPrefix, MatLabel, MatSuffix } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'unite-create-collaboration',
    templateUrl: './create-collaboration.component.html',
    styleUrls: ['./create-collaboration.component.scss'],
    imports: [ReactiveFormsModule, MatIcon, TranslateModule, MatFormField, MatPrefix, MatLabel, MatInput, MatAutocompleteTrigger, MatProgressSpinner, MatSuffix, MatAutocomplete, MatOption, MatList, MatListItem, MatListItemIcon, MatLine, MatIconButton, AddressInputV2Component, CustomerSettingsComponent, LoadingButtonComponent]
})
export class CreateCollaborationComponent implements OnInit {

  private destroyRef = inject(DestroyRef);
  memberId = input<string>();
  collaboration = output<Collaboration>();

  collaborationGroup: UntypedFormGroup;

  loadingCreate = false;

  // Members
  searchMemberControl: FormControl;
  searchMemberControlIsLoading = false;
  filteredMembers: Customer[];
  members: Member[] = [];

  constructor(
    private el: ElementRef,
    private googleAnalyticsService: GoogleAnalyticsService,
    private formBuilder: UntypedFormBuilder,
    private customersService: CustomersService,
    private collaborationsService: CollaborationsService) {
    this.collaborationGroup = this.formBuilder.group({
      name: [, [Validators.required]],
      email: [],
      mobile: [],
      phone: [],
      iban: [],
      businessunit: [],
      address: createAddressFormGroup(this.formBuilder),
      adviser: [],
      existing: [false],
    });



    this.searchMemberControl = this.formBuilder.control('');
    this.searchMemberControl.valueChanges.pipe(
      tap(() => this.searchMemberControlIsLoading = true),
      debounceTime(350),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
      switchMap((value: any) => {
        if (value && typeof value === 'string' && value.trim() !== '') {
          return this.customersService.getCustomers({
            name: value,
            types: ['Person', 'Company']
          }).pipe(map(data => data.items), catchError(() => of(null)));
        } else {
          this.searchMemberControlIsLoading = false;
          return of(null);
        }
      })).subscribe((filteredMembers: Customer[]) => {
        // Remove the filtered customers that are already chosen
        this.filteredMembers = _.differenceWith(filteredMembers, this.members, (filteredMemer: Customer, member: Member) => {
          return filteredMemer.id === member.customerId;
        });

        this.searchMemberControlIsLoading = false;
      });
  }

  ngOnInit() {
    if (this.memberId()) {
      this.customersService.getCustomerById({ id: this.memberId() }).subscribe(customer => {
        this.filteredMembers = [customer];
        this.addCustomerAsMember(customer);
      });
    }
  }

  create() {
    this.collaborationGroup.markAllAsTouched();

    if (this.collaborationGroup.valid) {
      this.googleAnalyticsService.trackEvent('customer', 'add', 'collaboration');
      this.loadingCreate = true;
      const collaboration = <NewCollaboration>this.collaborationGroup.value;
      collaboration.members = this.members;
      this.collaborationsService.addCollaboration({ body: collaboration }).pipe(finalize(() => {
        this.loadingCreate = false;
      })).subscribe((response: GuidId) => {
        collaboration.id = response.id;
        this.collaboration.emit(collaboration);
      });
    } else {
      this.scrollToFirstInvalidControl();
    }
  }

  addMember(matAutocompleteSelectedEvent: MatAutocompleteSelectedEvent) {
    if (matAutocompleteSelectedEvent.option.value) {
      const customer: Customer = matAutocompleteSelectedEvent.option.value;
      this.addCustomerAsMember(customer);
    }
    this.searchMemberControl.setValue('', { emitEvent: false });
  }

  private addCustomerAsMember(customer: Customer) {
    this.members.push({ customerId: customer.id, name: customer.name, type: customer.type, share: 0 });

    this.generateKlantnaam();

    this.members.map((member: Member) => {
      member.share = Math.round(100 / this.members.length);
    });

    // Add the contact information of the first member to the collaboration
    if (!this.collaborationGroup.get('email').value) {
      this.collaborationGroup.get('email').setValue(customer.email, { emitValue: false });
    }
    if (!this.collaborationGroup.get('mobile').value) {
      this.collaborationGroup.get('mobile').setValue(customer.mobile, { emitValue: false });
    }
    if (!this.collaborationGroup.get('phone').value) {
      this.collaborationGroup.get('phone').setValue(customer.phone, { emitValue: false });
    }
    if (!this.collaborationGroup.get('address').get('street').value) {
      this.collaborationGroup.get('address').patchValue(customer.address, { emitValue: false });
    }
  }

  changeShare(member: Member, share: number | string) {
    this.members[this.members.indexOf(member)].share = +share;
  }

  generateKlantnaam(): void {
    this.collaborationGroup.get('name').setValue('');
    this.members.forEach((member: Member, index) => {
      if (index !== 0) {
        this.collaborationGroup.get('name').setValue(this.collaborationGroup.value.name + ' - ');
      }
      this.collaborationGroup.get('name').setValue(this.collaborationGroup.value.name + member.name);
    });
  }

  removeMember(member: Member) {
    this.members.splice(this.members.indexOf(member), 1);

    this.members.map((memberUpdate: Member) => {
      memberUpdate.share = Math.round(100 / this.members.length);
    });

    this.generateKlantnaam();
  }

  displaySearchValue(customer: Customer) {
    if (customer) { return customer.name; }
  }

  private scrollToFirstInvalidControl() {
    const firstInvalidControl: HTMLElement = this.el.nativeElement.querySelector(
      "form .ng-invalid"
    );

    firstInvalidControl.scrollIntoView();
  }

}
